import React, { useState, useContext, useMemo } from 'react';
import { Button, DialogActions, Grid, TextField, IconButton, Paper, MenuItem, CircularProgress } from '@mui/material';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import { AlertMessagesContext } from 'react-alert-messages';
import TableService from 'services/TableService';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#afcdf0' : '#afcdf0',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));
export const getGeneratedTableNames = (name, count, customStart) => {
  const trimmed = name?.trim();
  const total = parseInt(count, 10);
  if (!trimmed || !total || total < 1) return [];

  if (total === 1) return [trimmed];

  const match = trimmed.match(/^(.*?)(\d+)$/);
  const prefix = match ? match[1] : `${trimmed} `;
  const defaultStart = match ? parseInt(match[2], 10) : 1;

  const parsedStart = parseInt(customStart, 10);
  const startNum = !isNaN(parsedStart) ? parsedStart : defaultStart;

  return Array.from({ length: total }, (_, i) => `${prefix}${startNum + i}`);
};

export default function TableFormDialog({ handleClose, handleSuccess, areas = [] }) {
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');
  const [data, setData] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [creatingProgress, setCreatingProgress] = useState(null);
  const [area, setArea] = useState(null);
  const [chair, setChair] = useState('');
  const [numberOfTables, setNumberOfTables] = useState(1);
  const [startNumber, setStartNumber] = useState('');
  const [manualStartNumber, setManualStartNumber] = useState(false);

  const { postAlertMessage } = useContext(AlertMessagesContext);

  const isMultiple = numberOfTables > 1;

  const generatedNames = useMemo(() => {
    return getGeneratedTableNames(data.name, numberOfTables, startNumber);
  }, [data.name, numberOfTables, startNumber]);

  const previewText = useMemo(() => {
    if (!generatedNames.length || !isMultiple) return '';
    if (generatedNames.length <= 5) {
      return generatedNames.join(', ');
    }
    return `${generatedNames.slice(0, 3).join(', ')} ... ${generatedNames[generatedNames.length - 1]} (${
      generatedNames.length
    } ${tCommon('Tables')})`;
  }, [generatedNames, isMultiple, tCommon]);

  const handleCreateTable = async () => {
    if (area === null) {
      postAlertMessage({ text: tPopup('Please select an area'), type: 'failed' });
      return;
    }

    const count = parseInt(numberOfTables, 10);
    if (isNaN(count) || count < 1) {
      postAlertMessage({ text: tPopup('Please enter a valid number of tables'), type: 'failed' });
      return;
    }

    if (!data.name || data.name.trim() === '') {
      postAlertMessage({ text: tPopup('Please enter a table name'), type: 'failed' });
      return;
    }

    const tableNames = getGeneratedTableNames(data.name, count, startNumber);
    if (!tableNames.length) {
      postAlertMessage({ text: tPopup('Please enter a table name'), type: 'failed' });
      return;
    }

    setLoading(true);
    let createdCount = 0;

    try {
      if (count === 1) {
        const payload = {
          name: tableNames[0],
          order: data.order,
          area_id: area.id,
          chairs: data.chairs || [],
        };
        await TableService.createTables(payload);
        postAlertMessage({ text: tPopup('Table created successfully'), type: 'success' });
      } else {
        for (let i = 0; i < count; i++) {
          setCreatingProgress(`${i + 1}/${count}`);
          const payload = {
            name: tableNames[i],
            order: data.order !== '' && data.order !== undefined ? Number(data.order) + i : undefined,
            area_id: area.id,
            chairs: (data.chairs || []).map((c) => ({ ...c })),
          };
          await TableService.createTables(payload);
          createdCount++;
        }
        postAlertMessage({
          text: tPopup('Tables created successfully', {
            defaultValue: `${count} tables created successfully`,
          }),
          type: 'success',
        });
      }
      await handleSuccess();
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
      if (createdCount > 0) {
        await handleSuccess();
      }
    } finally {
      setLoading(false);
      setCreatingProgress(null);
    }
  };

  const onAddChairClicked = () => {
    if (!chair || !chair.trim()) return;
    const currentChairs = data.chairs ? [...data.chairs] : [];
    currentChairs.push({ name: chair.trim() });
    setData({
      ...data,
      chairs: currentChairs,
    });
    setChair('');
  };

  return (
    <>
      <div className={styles.popUp}>
        <div className={styles.contentWrapper}>
          <div className={styles.headSec}>
            <h2 className={styles.editTitle}>{isMultiple ? tCommon('Add Tables') : tCommon('Add Table')}</h2>
            <CloseIcon onClick={handleClose} />
          </div>
          <div className={styles.inputSec}>
            <Grid container className={styles.inputRow} alignItems="center">
              <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Table Name')}</h3>
              </Grid>
              <Grid item xs={8} sm={8}>
                <TextField
                  label={tCommon('Table Name')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="text"
                  value={data.name || ''}
                  onChange={(e) => {
                    const newName = e.target.value.toUpperCase();
                    setData({ ...data, name: newName });
                    if (!manualStartNumber) {
                      const match = newName.trim().match(/^(.*?)(\d+)$/);
                      setStartNumber(match ? parseInt(match[2], 10) : 1);
                    }
                  }}
                  helperText={isMultiple ? tCommon('Base name or prefix (e.g. TABLE, T-, T1)') : ''}
                />
              </Grid>
            </Grid>
            <Grid container className={styles.inputRow} alignItems="center">
              <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Number of Tables')}</h3>
              </Grid>
              <Grid item xs={8} sm={8}>
                <NumberField
                  label={tCommon('Number of Tables')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={numberOfTables}
                  onChange={(e) => setNumberOfTables(e.target.value)}
                  InputProps={{
                    inputProps: { min: 1, max: 100 },
                  }}
                />
              </Grid>
            </Grid>
            {isMultiple && (
              <Grid container className={styles.inputRow} alignItems="center">
                <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                  <h3 className={styles.inputLabel}>{tCommon('Start Number')}</h3>
                </Grid>
                <Grid item xs={8} sm={8}>
                  <NumberField
                    label={tCommon('Start Number')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={startNumber}
                    onChange={(e) => {
                      setManualStartNumber(true);
                      setStartNumber(e.target.value);
                    }}
                    InputProps={{
                      inputProps: { min: 0 },
                    }}
                  />
                </Grid>
              </Grid>
            )}
            {isMultiple && previewText && (
              <Grid container className={styles.inputRow} alignItems="center">
                <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                  <h3 className={styles.inputLabel} style={{ fontSize: '13px', color: '#666' }}>
                    {tCommon('Preview')}
                  </h3>
                </Grid>
                <Grid item xs={8} sm={8}>
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#0d6efd',
                      backgroundColor: '#e7f1ff',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #b6d4fe',
                      lineHeight: '1.4',
                      wordBreak: 'break-word',
                    }}
                  >
                    <strong>{previewText}</strong>
                  </div>
                </Grid>
              </Grid>
            )}
            <Grid container className={styles.inputRow} alignItems="center">
              <Grid item xs={4} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Area')}</h3>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  select
                  label={tCommon('Select Area')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={area?.id || ''}
                  onChange={(e) => {
                    const selectedArea = (areas || []).find((a) => a.id === Number(e.target.value));
                    setArea(selectedArea);
                  }}
                >
                  {(areas || []).map((a) => (
                    <MenuItem key={a?.id} value={a?.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Grid container className={styles.inputRow} alignItems="center">
              <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{isMultiple ? tCommon('Starting Order') : tCommon('Order')}</h3>
              </Grid>
              <Grid item xs={8} sm={8}>
                <NumberField
                  label={isMultiple ? tCommon('Starting Order') : tCommon('Order')}
                  value={data.order !== undefined && data.order !== null ? data.order : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setData({
                      ...data,
                      order: val === '' ? '' : Number(val),
                    });
                  }}
                  helperText={
                    isMultiple && data.order !== '' && data.order !== undefined
                      ? `${tCommon('Orders will be')}: ${data.order} - ${Number(data.order) + Number(numberOfTables) - 1}`
                      : ''
                  }
                />
              </Grid>
            </Grid>
            {(data.chairs || []).map((chairItem, index) => (
              <Grid container className={styles.inputRow} alignItems="center" key={index}>
                <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                  <h6 className={styles.tableName}>{tCommon('Chair {{number}}', { number: index + 1 })}</h6>
                </Grid>
                <Grid item xs={6}>
                  <Item>{chairItem.name} </Item>
                </Grid>
                <Grid item xs={2} display="flex" justifyContent="center">
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => {
                      const updated = [...data.chairs];
                      updated.splice(index, 1);
                      setData({
                        ...data,
                        chairs: updated,
                      });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Grid container className={styles.inputRow} alignItems="center">
              <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                <h6 className={styles.tableName}>{tCommon('New Chair')}</h6>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={tCommon('Chair Name')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={chair}
                  type="text"
                  onChange={(e) => {
                    setChair(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      onAddChairClicked();
                    }
                  }}
                />
              </Grid>
              <Grid item xs={2} display="flex" justifyContent="center">
                <IconButton
                  fullWidth
                  edge="end"
                  aria-label="add"
                  disabled={!chair || !chair.trim()}
                  onClick={onAddChairClicked}
                >
                  <LibraryAddIcon />
                </IconButton>
              </Grid>
            </Grid>
            <DialogActions>
              <Button variant="contained" color="secondary" size="small" onClick={handleClose}>
                {tCommon('Close')}
              </Button>
              <Button
                variant="contained"
                size="small"
                style={{ backgroundColor: '#00a65a' }}
                disabled={!data.name || data.name.trim() === '' || !numberOfTables || numberOfTables < 1 || isLoading}
                onClick={handleCreateTable}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                    {creatingProgress
                      ? tCommon('Creating ({{progress}})...', {
                          progress: creatingProgress,
                          defaultValue: `Creating (${creatingProgress})...`,
                        })
                      : tCommon('Creating...')}
                  </>
                ) : (
                  tCommon('Create')
                )}
              </Button>
            </DialogActions>
          </div>
        </div>
      </div>
    </>
  );
}
